import { supabase } from '../supabaseClient';
import type {
  ApplyResult,
  AutoScheduleGateway,
  AvailabilityWindow,
  DateRange,
  ExistingSlot,
  NightWorkRule,
  PlanPreview,
  PlanViolation,
  ScheduleContext,
  ScheduleGuard,
  SchedulePlanPayload,
  ScheduleRule,
  ScheduleSite,
  ScheduleSiteContext,
  TimeWindow,
  WorkedShift,
} from './autoSchedule';
import type { BreakRequirement } from './rosterCompliance';

type Row = Record<string, unknown>;

const numberOrNull = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const stringOrNull = (value: unknown): string | null => (
  typeof value === 'string' && value.length > 0 ? value : null
);

const rows = (value: unknown): Row[] => (Array.isArray(value) ? value as Row[] : []);

const strings = (value: unknown): string[] => (
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
);

function toBreakRequirements(value: unknown): BreakRequirement[] {
  return rows(value).flatMap((tier) => {
    const workMinutesOver = numberOrNull(tier.work_minutes_over);
    const minimumBreakMinutes = numberOrNull(tier.minimum_break_minutes);
    return workMinutesOver !== null && minimumBreakMinutes !== null ? [{ workMinutesOver, minimumBreakMinutes }] : [];
  });
}

/** Night work applies only when the row defines the period and what makes a shift a night shift. */
function toNightRule(row: Row): NightWorkRule | null {
  const periodStartMinute = numberOrNull(row.night_period_start_minute);
  const periodEndMinute = numberOrNull(row.night_period_end_minute);
  const minimumNightMinutes = numberOrNull(row.night_worker_min_night_minutes);
  if (periodStartMinute === null || periodEndMinute === null || minimumNightMinutes === null) return null;

  return {
    periodStartMinute,
    periodEndMinute,
    minimumNightMinutes,
    maxShiftMinutes: numberOrNull(row.max_night_shift_minutes),
    maxAverageWeeklyMinutes: numberOrNull(row.max_average_night_weekly_minutes),
    maxAverage24hMinutes: numberOrNull(row.max_average_night_24h_minutes),
    // The evaluator's own fallback when the row names no reference period.
    referenceDays: Math.max(
      numberOrNull(row.night_work_reference_days) ?? ((numberOrNull(row.weekly_reference_weeks) ?? 17) * 7),
      1,
    ),
  };
}

function toRule(value: unknown): ScheduleRule | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Row;
  return {
    id: row.id as string,
    name: (row.name as string | null) ?? '',
    countryCode: ((row.country_code as string | null) ?? '').trim(),
    minimumRestMinutes: numberOrNull(row.minimum_rest_minutes),
    maxShiftMinutes: numberOrNull(row.max_shift_minutes),
    maxConsecutiveDays: numberOrNull(row.max_consecutive_days),
    maxAverageWeeklyMinutes: numberOrNull(row.max_average_weekly_minutes),
    weeklyReferenceWeeks: numberOrNull(row.weekly_reference_weeks),
    breakRequirements: toBreakRequirements(row.break_requirements),
    maxNightShiftMinutes: numberOrNull(row.max_night_shift_minutes),
    sourceUrls: strings(row.source_urls),
    night: toNightRule(row),
  };
}

function toSite(value: unknown): ScheduleSite {
  const row = (value ?? {}) as Row;
  return {
    id: row.id as string,
    name: (row.name as string | null) ?? '',
    countryCode: ((row.country_code as string | null) ?? '').trim(),
    timezone: (row.timezone as string | null) ?? 'UTC',
    coverageStartMinute: numberOrNull(row.coverage_start_minute) ?? 0,
    coverageEndMinute: numberOrNull(row.coverage_end_minute) ?? 1440,
    requiredGuardCount: Math.max(numberOrNull(row.required_guard_count) ?? 1, 1),
    requiredLicenceTypeId: stringOrNull(row.required_licence_type_id),
    requiredLicenceName: stringOrNull(row.required_licence_name),
  };
}

const toSlot = (row: Row): ExistingSlot => ({
  id: row.id as string,
  slotDate: row.slot_date as string,
  name: (row.name as string | null) ?? '',
  startMinute: numberOrNull(row.start_minute) ?? 0,
  endMinute: numberOrNull(row.end_minute) ?? 0,
  guardsRequired: numberOrNull(row.guards_required) ?? 1,
  isPublished: row.is_published === true,
  displayOrder: numberOrNull(row.display_order) ?? 0,
  requiredLicenceTypeId: stringOrNull(row.required_licence_type_id),
  guardIds: strings(row.guard_ids),
});

/** `[[start, end], ...]` minute pairs, as the context sends a shift's duty. */
function toSegments(value: unknown): TimeWindow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((pair) => {
    if (!Array.isArray(pair)) return [];
    const [startMinute, endMinute] = pair;
    return typeof startMinute === 'number' && typeof endMinute === 'number' ? [{ startMinute, endMinute }] : [];
  });
}

const toShift = (row: Row): WorkedShift => ({
  siteId: row.site_id as string,
  slotDate: row.slot_date as string,
  startMinute: numberOrNull(row.start_minute) ?? 0,
  endMinute: numberOrNull(row.end_minute) ?? 0,
  startMs: Number(row.start_ms),
  endMs: Number(row.end_ms),
  dutyMinutes: numberOrNull(row.duty_minutes) ?? 0,
  dutySegments: toSegments(row.duty_segments),
});

const AVAILABILITY_TYPES: AvailabilityWindow['type'][] = ['available', 'preferred', 'unavailable'];

function toGuard(row: Row): ScheduleGuard {
  const minHours = numberOrNull(row.contract_min_weekly_hours);
  const maxHours = numberOrNull(row.contract_max_weekly_hours);
  return {
    id: row.id as string,
    fullName: (row.full_name as string | null) ?? 'Unknown guard',
    avatarUrl: stringOrNull(row.avatar_url),
    contractMinWeeklyMinutes: minHours === null ? null : minHours * 60,
    contractMaxWeeklyMinutes: maxHours === null ? null : maxHours * 60,
    licences: rows(row.licences).flatMap((licence) => {
      const licenceTypeId = stringOrNull(licence.licence_type_id);
      const expiryDate = stringOrNull(licence.expiry_date);
      return licenceTypeId && expiryDate ? [{ licenceTypeId, expiryDate }] : [];
    }),
    leave: rows(row.leave).map((leave) => ({
      startDate: leave.start_date as string,
      endDate: leave.end_date as string,
    })),
    availability: rows(row.availability).flatMap((window) => {
      const type = window.type as AvailabilityWindow['type'];
      return AVAILABILITY_TYPES.includes(type)
        ? [{ type, startMs: Number(window.start_ms), endMs: Number(window.end_ms) }]
        : [];
    }),
    shifts: rows(row.shifts).map(toShift),
    siteShiftCounts: toCounts(row.site_shift_counts),
    // Absent from a database that predates bindings, which reads as bound to nothing.
    restrictedSiteIds: Array.isArray(row.restricted_site_ids) ? strings(row.restricted_site_ids) : null,
  };
}

function toCounts(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const counts: Record<string, number> = {};
  Object.entries(value as Row).forEach(([siteId, count]) => {
    if (typeof count === 'number') counts[siteId] = count;
  });
  return counts;
}

const toSiteContext = (row: Row): ScheduleSiteContext => ({
  site: toSite(row.site),
  today: row.today as string,
  rule: toRule(row.rule),
  slots: rows(row.slots).map(toSlot),
  publishedBreakDates: strings(row.published_break_dates),
});

const toViolation = (row: Row): PlanViolation => ({
  kind: row.kind === 'hard' ? 'hard' : 'rule',
  code: (row.code as string | null) ?? '',
  guardId: stringOrNull(row.guard_id),
  guardName: stringOrNull(row.guard_name),
  siteId: stringOrNull(row.site_id),
  slotDate: row.slot_date as string,
  dutySlotIds: strings(row.duty_slot_ids),
  message: (row.message as string | null) ?? '',
});

/** Supabase side of auto scheduling: the gateway the scheduling engine runs against. */
export const autoScheduleService: AutoScheduleGateway = {
  async loadContext(siteIds: string[], range: DateRange): Promise<ScheduleContext> {
    const { data, error } = await supabase.rpc('auto_schedule_context', {
      p_site_ids: siteIds,
      p_start_date: range.startDate,
      p_end_date: range.endDate,
    });
    if (error) throw error;

    const record = (data ?? {}) as Row;
    return {
      sites: rows(record.sites).map(toSiteContext),
      guards: rows(record.guards).map(toGuard),
    };
  },

  async previewPlan(siteIds: string[], plan: SchedulePlanPayload): Promise<PlanPreview> {
    const { data, error } = await supabase.rpc('preview_auto_schedule', {
      p_site_ids: siteIds,
      p_plan: plan,
    });
    if (error) throw error;
    return { violations: rows((data as Row | null)?.violations).map(toViolation) };
  },

  async applyPlan(siteIds: string[], plan: SchedulePlanPayload): Promise<ApplyResult> {
    const { data, error } = await supabase.rpc('apply_auto_schedule', {
      p_site_ids: siteIds,
      p_plan: plan,
    });
    if (error) throw error;

    const record = (data ?? {}) as Row;
    return {
      createdSlots: numberOrNull(record.created_slots) ?? plan.slots.length,
      createdAssignments: numberOrNull(record.created_assignments) ?? plan.assignments.length,
      createdBreaks: numberOrNull(record.created_breaks) ?? plan.breaks.length,
    };
  },
};
